import { apiClient } from "@/services/apiClient";
import { API_CONFIG } from "@/config/api";
import { MediaItem } from "@/core/MediaItem/MediaItem.model";
import { UserRole } from '@/services/urlService';
import {
  Post,
  CreatePostImage,
  CreatePostRequest,
  CreatePostResponse,
  UpdatePostRequest,
  UpdatePostResponse,
  DeletePostResponse,
  PostStatus,
  PostType,
  StatusUpdateResponse,
  StatusStatisticsResponse,
  VALID_STATUSES,
} from "@/core/types/postTypes";

export interface GetPostsOptions {
  gridId?: number;
  statuses?: string[];  // NEW: Array of status strings for filtering
  status?: PostStatus;  // Deprecated: Use statuses instead
  postType?: PostType;  // NEW: filter by post type (e.g. "story" for the Stories board)
  limit?: number;
  offset?: number;
}

export interface PostsByStatusResponse {
  posts: Post[];
  status: string;
  count: number;
}

export interface PostFilters {
  status?: PostStatus;
  isCarousel?: boolean;
  hasMedia?: boolean;
  dateFrom?: string;
  dateTo?: string;
}

class PostsService {
  private readonly apiUrl: string;

  constructor(apiUrl: string = API_CONFIG.URL) {
    this.apiUrl = apiUrl;
  }

  private processMediaItems(mediaItems: any[], clientId: string): MediaItem[] {
    const storedUser = localStorage.getItem("user");
    const accessToken = storedUser ? JSON.parse(storedUser).accessToken : undefined;

    return mediaItems.map((item) =>
      MediaItem.fromBackend(item, UserRole.ADMIN, clientId, accessToken),
    );
  }

  private processPost(post: any, clientId: string): Post {
    return {
      ...post,
      mediaItems: this.processMediaItems(post?.mediaItems || [], clientId),
    };
  }

  private processPosts(posts: any[], clientId: string): Post[] {
    return posts.map((post) => this.processPost(post, clientId));
  }

  private handleError(error: any, defaultMessage: string): never {
    console.error(`PostsService Error: ${defaultMessage}`, error);

    // If it's already a simple Error (from apiClient), just re-throw it
    if (error instanceof Error) {
      throw error;
    }

    // For other errors, use the default message
    throw new Error(defaultMessage);
  }

  async getClientPosts(clientId: string): Promise<Post[]> {
    try {
      const response = await apiClient.get<{ posts: any[] }>(
        `/client/${clientId}/posts`,
      );

      return this.processPosts(response.posts, clientId);
    } catch (error) {
      this.handleError(error, "Failed to fetch posts");
    }
  }

  async getClientPostsWithFilters(
    clientId: string,
    options: GetPostsOptions = {},
  ): Promise<Post[]> {
    try {
      const params = new URLSearchParams();

      if (options.gridId) {
        params.append("grid_id", options.gridId.toString());
      }

      // NEW: Add statuses as comma-separated list for backend filtering
      if (options.statuses?.length) {
        params.append("statuses", options.statuses.join(","));
      }

      // Deprecated: Support old status param for backwards compatibility
      if (options.status && !options.statuses) {
        params.append("statuses", options.status);
      }

      if (options.postType) {
        params.append("post_type", options.postType);
      }

      if (options.limit) {
        params.append("limit", options.limit.toString());
      }

      if (options.offset) {
        params.append("offset", options.offset.toString());
      }

      const queryString = params.toString();
      const url = `/client/${clientId}/posts${queryString ? `?${queryString}` : ""}`;

      const response = await apiClient.get<{ posts: any[] }>(url);

      return this.processPosts(response.posts, clientId);
    } catch (error) {
      this.handleError(error, "Failed to fetch filtered posts");
    }
  }

  async getPost(clientId: string, postId: string): Promise<Post> {
    try {
      const response = await apiClient.get<{ post: any }>(
        `/client/${clientId}/posts/${postId}`,
      );

      return this.processPost(response.post, clientId);
    } catch (error) {
      this.handleError(error, "Failed to fetch post");
    }
  }

  async createOrGetPost(
    clientId: string,
    postData: CreatePostRequest,
  ): Promise<CreatePostResponse> {
    try {
      const response = await apiClient.post<CreatePostResponse>(
        `/client/${clientId}/posts`,
        postData,
      );

      const processedPost = this.processPost(response.post, clientId);

      return {
        ...response,
        post: processedPost,
      };
    } catch (error) {
      this.handleError(error, "Failed to create or get post");
    }
  }

  async updatePost(
    clientId: string,
    postId: string,
    updateData: UpdatePostRequest,
  ): Promise<UpdatePostResponse> {
    try {
      const response = await apiClient.put<UpdatePostResponse>(
        `/client/${clientId}/posts/${postId}`,
        updateData,
      );

      const processedPost = this.processPost(response.post, clientId);

      return {
        ...response,
        post: processedPost,
      };
    } catch (error) {
      this.handleError(error, "Failed to update post");
    }
  }

  async updateReel(
    clientId: string,
    reelId: string,
    updateData: {
      videoMediaId?: number;
      thumbnailMediaId?: number;
      caption?: string;
      hashtags?: string[];
    },
  ): Promise<UpdatePostResponse> {
    try {
      const response = await apiClient.put<UpdatePostResponse>(
        `/client/${clientId}/posts/reel/${reelId}`,
        updateData,
      );

      const processedPost = this.processPost(response.post, clientId);

      return {
        ...response,
        post: processedPost,
      };
    } catch (error) {
      this.handleError(error, "Failed to update reel");
    }
  }

  async linkToIdea(
    clientId: string,
    postId: string,
    ideaId: string,
  ): Promise<Post> {
    try {
      const response = await apiClient.put<UpdatePostResponse>(
        `/client/${clientId}/posts/${postId}`,
        { ideaId },
      );

      return this.processPost(response.post, clientId);
    } catch (error) {
      this.handleError(error, "Failed to link post to idea");
    }
  }

  async unlinkFromIdea(
    clientId: string,
    postId: string,
  ): Promise<Post> {
    try {
      const response = await apiClient.put<UpdatePostResponse>(
        `/client/${clientId}/posts/${postId}`,
        { ideaId: null },
      );

      return this.processPost(response.post, clientId);
    } catch (error) {
      this.handleError(error, "Failed to unlink post from idea");
    }
  }

  async deletePost(
    clientId: string,
    postId: string,
  ): Promise<DeletePostResponse> {
    try {
      const response = await apiClient.delete<DeletePostResponse>(
        `/client/${clientId}/posts/${postId}`,
      );

      return response;
    } catch (error) {
      this.handleError(error, "Failed to delete post");
    }
  }

  async updatePostStatus(
    clientId: string,
    postId: string,
    newStatus: PostStatus,
  ): Promise<StatusUpdateResponse> {
    try {
      const response = await apiClient.put<StatusUpdateResponse>(
        `/client/${clientId}/posts/${postId}/status`,
        { status: newStatus },
      );

      return {
        ...response,
        post: this.processPost(response.post, clientId),
      };
    } catch (error) {
      this.handleError(error, "Failed to update post status");
    }
  }

  async retryPostPreparation(
    clientId: string,
    postId: string,
  ): Promise<{
    success: boolean;
    message: string;
    postId: string;
    post?: Post;
  }> {
    try {
      const response = await apiClient.post(
        `/client/${clientId}/posts/${postId}/retry-preparation`,
        {},
      );

      // Process the returned post if present
      if (response.post) {
        response.post = this.processPost(response.post, clientId);
      }

      return response;
    } catch (error) {
      this.handleError(error, "Failed to retry post preparation");
    }
  }

  async getPostStatus(
    clientId: string,
    postId: string,
  ): Promise<{
    postId: string;
    status: PostStatus;
    validStatuses: PostStatus[];
    post: Post;
  }> {
    try {
      const response = await apiClient.get(
        `/client/${clientId}/posts/${postId}/status`,
      );

      return {
        ...response,
        post: this.processPost(response.post, clientId),
      };
    } catch (error) {
      this.handleError(error, "Failed to fetch post status");
    }
  }

  async getPostsByStatus(
    clientId: string,
    status: PostStatus,
  ): Promise<Post[]> {
    try {
      const response = await apiClient.get<PostsByStatusResponse>(
        `/client/${clientId}/posts/status/${status}`,
      );

      return this.processPosts(response.posts, clientId);
    } catch (error) {
      this.handleError(error, "Failed to fetch posts by status");
    }
  }

  async getStatusStatistics(
    clientId: string,
  ): Promise<StatusStatisticsResponse> {
    try {
      const response = await apiClient.get<StatusStatisticsResponse>(
        `/client/${clientId}/posts/statistics`,
      );

      return response;
    } catch (error) {
      this.handleError(error, "Failed to fetch status statistics");
    }
  }

  async bulkUpdatePostStatus(
    clientId: string,
    postIds: string[],
    newStatus: PostStatus,
  ): Promise<{
    success: boolean;
    updatedCount: number;
    failedPosts: string[];
    results: StatusUpdateResponse[];
  }> {
    try {
      const response = await apiClient.put(
        `/client/${clientId}/posts/bulk-status`,
        {
          postIds: postIds,
          status: newStatus,
        },
      );

      return {
        ...response,
        results: response.results.map((result: any) => ({
          ...result,
          post: this.processPost(result.post, clientId),
        })),
      };
    } catch (error) {
      this.handleError(error, "Failed to bulk update post statuses");
    }
  }

  async searchPosts(
    clientId: string,
    query: string,
    filters: PostFilters = {},
  ): Promise<Post[]> {
    try {
      const params = new URLSearchParams();
      params.append("q", query);

      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });

      const response = await apiClient.get<{ posts: any[] }>(
        `/client/${clientId}/posts/search?${params.toString()}`,
      );

      return this.processPosts(response.posts, clientId);
    } catch (error) {
      this.handleError(error, "Failed to search posts");
    }
  }

  async filterPosts(clientId: string, filters: PostFilters): Promise<Post[]> {
    try {
      const response = await apiClient.post<{ posts: any[] }>(
        `/client/${clientId}/posts/filter`,
        filters,
      );

      return this.processPosts(response.posts, clientId);
    } catch (error) {
      this.handleError(error, "Failed to filter posts");
    }
  }

  async reorderPostMedia(
    clientId: string,
    postId: string,
    mediaOrder: { id: number; position: number }[],
  ): Promise<{
    success: boolean;
    message: string;
    post: Post;
  }> {
    try {
      const response = await apiClient.put(
        `/client/${clientId}/posts/${postId}/media/reorder`,
        { mediaOrder },
      );

      return {
        ...response,
        post: this.processPost(response.post, clientId),
      };
    } catch (error) {
      this.handleError(error, "Failed to reorder post media");
    }
  }

  async duplicatePost(
    clientId: string,
    postId: string,
    newPostId?: string,
  ): Promise<CreatePostResponse> {
    try {
      const originalPost = await this.getPost(clientId, postId);

      const duplicateData: CreatePostRequest = {
        postId: newPostId || `${originalPost.id}-copy-${Date.now()}`,
        groupName: originalPost.groupName,
        postType: originalPost.postType,
        caption: originalPost.caption,
        hashtags: originalPost.hashtags,
        images: originalPost.mediaItems.map(
          (item): CreatePostImage => item.toCreatePostImage(),
        ),
      };

      return this.createOrGetPost(clientId, duplicateData);
    } catch (error) {
      this.handleError(error, "Failed to duplicate post");
    }
  }

  async getPostAnalytics(
    clientId: string,
    postId?: string,
  ): Promise<{
    totalPosts: number;
    byStatus: Record<string, number>;
    byType: Record<string, number>;
    mediaStats: {
      totalMediaItems: number;
      photos: number;
      videos: number;
      carouselPosts: number;
    };
  }> {
    try {
      const url = postId
        ? `/client/${clientId}/posts/${postId}/analytics`
        : `/client/${clientId}/posts/analytics`;

      const response = await apiClient.get(url);
      return response;
    } catch (error) {
      this.handleError(error, "Failed to fetch post analytics");
    }
  }

  async exportPosts(
    clientId: string,
    format: "json" | "csv" = "json",
    filters: PostFilters = {},
  ): Promise<{
    downloadUrl: string;
    filename: string;
    expiresAt: string;
  }> {
    try {
      const response = await apiClient.post(
        `/client/${clientId}/posts/export`,
        { format, filters },
      );

      return response;
    } catch (error) {
      this.handleError(error, "Failed to export posts");
    }
  }

  async schedulePost(
    clientId: string,
    postId: string,
    localDateTime: string,
    timezone: string = "Europe/Rome",
  ): Promise<{
    success: boolean;
    scheduledPostId: string;
    scheduledFor: string;
    scheduledForLocal: string;
    timezone: string;
    message: string;
  }> {
    try {
      // Send local datetime string (e.g., "2025-01-12T11:00") with timezone
      // Backend will parse this in the specified timezone and convert to UTC
      const response = await apiClient.post(
        `/api/${clientId}/posts/${postId}/schedule`,
        {
          scheduledFor: localDateTime,
          timezone,
        },
      );

      return response;
    } catch (error) {
      this.handleError(error, "Failed to schedule post");
    }
  }

  async cancelSchedule(
    clientId: string,
    postId: string,
  ): Promise<{
    success: boolean;
    message: string;
  }> {
    try {
      const response = await apiClient.delete(
        `/api/${clientId}/posts/${postId}/schedule`,
      );

      return response;
    } catch (error) {
      this.handleError(error, "Failed to cancel schedule");
    }
  }

  async getScheduledPosts(clientId: string): Promise<{
    id: string;
    clientId: string;
    postId: string;
    scheduledFor: string;
    status: string;
    attemptCount: number;
    clientTimezone: string;
    maxAttempts: number;
    lastAttemptAt: string | null;
    lastError: any | null;
    preparationExecutionId: string | null;
    publishExecutionId: string | null;
    createdAt: string;
    updatedAt: string;
  }[]> {
    try {
      const response = await apiClient.get<{
        scheduledPosts: any[];
      }>(`/api/${clientId}/scheduled-posts`);

      return response.scheduledPosts;
    } catch (error) {
      this.handleError(error, "Failed to fetch scheduled posts");
    }
  }

  getValidStatuses(): PostStatus[] {
    return [...VALID_STATUSES];
  }

  isValidStatus(status: string): status is PostStatus {
    return VALID_STATUSES.includes(status as PostStatus);
  }

  getNextStatus(currentStatus: PostStatus): PostStatus | null {
    const currentIndex = VALID_STATUSES.indexOf(currentStatus);
    if (currentIndex === -1 || currentIndex === VALID_STATUSES.length - 1) {
      return null;
    }
    return VALID_STATUSES[currentIndex + 1];
  }

  getPreviousStatus(currentStatus: PostStatus): PostStatus | null {
    const currentIndex = VALID_STATUSES.indexOf(currentStatus);
    if (currentIndex <= 0) {
      return null;
    }
    return VALID_STATUSES[currentIndex - 1];
  }

  async getScheduleStatus(clientId: string, postId: string): Promise<any> {
    const response = await apiClient.get(
      `${this.apiUrl}/api/${clientId}/posts/${postId}/schedule-status`
    );
    return response.data;
  }
}

export const postsService = new PostsService();

export default postsService;
export { PostsService };
